// services/api.js - Frontend API Integration
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || "https://dashboard.lono.co.in/api";

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add a request interceptor to include the token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token"); // Or wherever you store your token
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);
// Banking Partner API
export const bankingPartnerAPI = {
  // Get all banking partners
  getAll: async () => {
    try {
      const response = await api.get("/banking-partners");
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get banking partner by ID
  getById: async (id) => {
    try {
      const response = await api.get(`/banking-partners/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Create new banking partner
  create: async (bankingPartnerData) => {
    try {
      const response = await api.post("/banking-partners", bankingPartnerData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update banking partner
  // Update banking partner
  update: async (id, data) => {
    try {
      const response = await api.put(`/banking-partners/${id}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete banking partner
  delete: async (id) => {
    try {
      const response = await api.delete(`/banking-partners/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  // Fetches all applications for a specific banking partner, filtered by status.
  getAllApplications: async (bankId, status) => {
    try {
      if (!bankId) {
        return Promise.reject(new Error("Bank ID is required."));
      }
      const response = await api.get(
        `/banking-partners/${bankId}/applications`, // Fixed endpoint to match route
        { params: { status } } // Pass status as a query parameter
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching bank applications:", error);
      throw error;
    }
  },
  // Get loan details by distribution ID
  getLoanDetailsByDistributionId: async (distributionId) => {
    try {
      const response = await api.get(
        `/loan-distribution/${distributionId}/loan`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Upload document
  uploadDocument: async (file, documentType, bankingPartnerId) => {
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append("document", file);
      formData.append("documentType", documentType);
      formData.append("bankingPartnerId", bankingPartnerId);

      const response = await axios.post(
        `${API_URL}/banking-partners/upload`,
        formData,
        {
          headers: {
            // This might be redundant since we have a global interceptor now
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  // Banking partner specific endpoints
  respondToLoanDistribution: async (distributionId, responseData) => {
    console.log("Responding to loan distribution with ID:", distributionId);
    try {
      const response = await api.patch(
        // Assuming backend route changes to /loan-response/distribution/:distributionId
        `/loan-response/${distributionId}`,
        responseData
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  // Fetch pie chart data for a banking partner
  getPieChartData: async (bankingPartnerId) => {
    try {
      const response = await api.get(
        `/banking-partners/${bankingPartnerId}/pie-chart-data`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  // Fetch dashboard card stats for a banking partner
  getDashboardCardStats: async (bankingPartnerId) => {
    try {
      const response = await api.get(
        `/banking-partners/${bankingPartnerId}/dashboard-card-stats`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  // Fetch segment breakdown for all banking partners
  getSegmentBreakdown: async () => {
    try {
      const response = await api.get(`/banking-partners/segment-breakdown`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  // Fetch compliance documents for a banking partner
  getComplianceDocuments: async (bankingPartnerId) => {
    try {
      const response = await api.get(`/banking-partners/${bankingPartnerId}/compliance-documents`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  // Fetch dashboard stats and trends for a banking partner
  getDashboardStatsWithTrends: async (bankingPartnerId) => {
    try {
      const response = await api.get(`/banking-partners/${bankingPartnerId}/dashboard-stats-trends`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  // Update loan status for a specific loan in loanDetails
  updateLoanStatus: async (partnerId, loanIndex, status) => {
    try {
      const response = await api.patch(`/banking-partners/${partnerId}/loan/${loanIndex}/status`, { status });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  // Update insurance status for a specific insurance in insuranceDetails
  updateInsuranceStatus: async (partnerId, insuranceIndex, status) => {
    try {
      const response = await api.patch(`/banking-partners/${partnerId}/insurance/${insuranceIndex}/status`, { status });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  // Add a new loan to a banking partner
  addLoan: async (partnerId, loanData) => {
    try {
      const response = await api.post(`/banking-partners/${partnerId}/loans`, loanData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  // Add a new insurance to a banking partner
  addInsurance: async (partnerId, insuranceData) => {
    try {
      const response = await api.post(`/banking-partners/${partnerId}/insurances`, insuranceData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  // Add a new loan category
  addLoanCategory: async (partnerId, categoryName) => {
    try {
      const response = await api.post(`/banking-partners/${partnerId}/loan-categories`, { categoryName });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

// Loan Type API
export const loanTypeAPI = {
  // Get all loan types
  getAll: async () => {
    try {
      const response = await api.get("/loan-types");
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Create new loan type
  create: async (loanTypeData) => {
    try {
      const response = await api.post("/loan-types", loanTypeData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};
